import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Filter, X } from 'lucide-react';
import ProductCard from '../components/Products/ProductCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const selectedCategory = searchParams.get('category') || '';

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const url = selectedCategory 
        ? `${API}/products?category=${selectedCategory}`
        : `${API}/products`;
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    if (category) {
      setSearchParams({ category });
    } else {
      setSearchParams({});
    }
    setShowFilters(false);
  };

  return (
    <div className="min-h-screen section-cream" data-testid="products-page">
      {/* Page Header */}
      <div className="section-pink py-16">
        <div className="container-custom text-center">
          <span className="text-accent uppercase tracking-widest text-sm font-medium mb-4 block">
            Collection
          </span>
          <h1 className="font-serif text-4xl md:text-5xl text-text-primary">
            {selectedCategory && categories[selectedCategory] 
              ? categories[selectedCategory] 
              : 'Tous nos produits'}
          </h1>
          <p className="text-text-secondary mt-4 max-w-lg mx-auto">
            Découvrez notre sélection de parfums et soins d'exception
          </p>
        </div>
      </div>

      <div className="container-custom py-12">
        {/* Filters Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
          {/* Category Pills - Desktop */}
          <div className="hidden md:flex flex-wrap gap-3">
            <button
              onClick={() => handleCategoryChange('')}
              className={`category-pill ${!selectedCategory ? 'active' : ''}`}
              data-testid="filter-all"
            >
              Tous
            </button>
            {Object.entries(categories).map(([key, value]) => (
              <button
                key={key}
                onClick={() => handleCategoryChange(key)}
                className={`category-pill ${selectedCategory === key ? 'active' : ''}`}
                data-testid={`filter-${key}`}
              >
                {value}
              </button>
            ))}
          </div>

          {/* Mobile Filter Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden flex items-center gap-2 category-pill"
            data-testid="mobile-filter-btn"
          >
            <Filter className="w-4 h-4" />
            Filtrer
          </button>

          {/* Results Count */}
          <p className="text-text-secondary text-sm">
            {products.length} produit{products.length > 1 ? 's' : ''}
          </p>
        </div>

        {/* Mobile Filters Panel */}
        {showFilters && (
          <div className="md:hidden fixed inset-0 z-50 bg-white" data-testid="mobile-filters-panel">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-serif text-lg text-text-primary">Catégories</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="p-2"
                data-testid="close-filters"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <button
                onClick={() => handleCategoryChange('')}
                className={`w-full text-left px-4 py-3 transition-colors ${
                  !selectedCategory ? 'bg-surface text-accent' : 'text-text-secondary hover:bg-surface'
                }`}
              >
                Tous les produits
              </button>
              {Object.entries(categories).map(([key, value]) => (
                <button
                  key={key}
                  onClick={() => handleCategoryChange(key)}
                  className={`w-full text-left px-4 py-3 transition-colors ${
                    selectedCategory === key ? 'bg-surface text-accent' : 'text-text-secondary hover:bg-surface'
                  }`}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Products Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20" data-testid="no-products">
            <p className="text-text-secondary text-lg">
              Aucun produit trouvé dans cette catégorie.
            </p>
          </div>
        ) : (
          <div className="product-grid stagger-children" data-testid="products-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
